import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Star, Search } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/Auth";
import { ShopSkeleton } from "@/components/products/skeletons";
import { formatCurrency } from "@/utils/format";
import Seo from "../../seo/seo";
import { routeSeoConfig } from "@/utils/seo-config";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchAllProducts,
  setSearchTerm,
  setCurrentPage,
  setProductsPerPage,
  setFilters,
  selectPaginatedProducts,
  selectProductsLoading,
  selectTotalPages,
  selectPagination,
  selectSearchTerm,
  selectFilters,
} from "@/store/slices/productSlice";
import { addToCart as addToCartAction } from "@/store/slices/cartSlice";

export default function Shop() {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  
  // Redux selectors
  const products = useAppSelector(selectPaginatedProducts);
  const loading = useAppSelector(selectProductsLoading);
  const totalPages = useAppSelector(selectTotalPages);
  const pagination = useAppSelector(selectPagination);
  const searchTerm = useAppSelector(selectSearchTerm);
  const filters = useAppSelector(selectFilters);
  const currentPage = pagination.currentPage;

  // Fetch products on mount
  useEffect(() => {
    dispatch(fetchAllProducts());
  }, [dispatch]);

  // Set products per page
  useEffect(() => {
    dispatch(setProductsPerPage(8));
  }, [dispatch]);

  // Read search query parameter and set it in Redux
  useEffect(() => {
    const searchQuery = searchParams.get("search");
    if (searchQuery) {
      dispatch(setSearchTerm(searchQuery));
    }
  }, [searchParams, dispatch]);

  const handleAddToCart = (product) => {
    const hasDiscount = product.discount > 0;
    const finalPrice = hasDiscount
      ? product.price - (product.price * product.discount) / 100
      : product.price;

    dispatch(
      addToCartAction({
        product: {
          ...product,
          productId: product.productId,
          price: Number(finalPrice), // ✅ discounted price goes into cart
          image: product.image,
        },
        quantity: 1,
        user,
      })
    );
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      dispatch(setCurrentPage(pageNumber));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSearchChange = (event) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleCategoryChange = (category) => {
    dispatch(setFilters({ category: category || '' }));
  };

  const categories = [
    { label: 'All', value: '' },
    { label: 'Skin', value: 'Skin' },
    { label: 'Hair', value: 'Hair' },
  ];

  return (
    <>
      <Seo {...routeSeoConfig.shop} type="website" />
    <div>
      <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-8">
          All Products
        </h1>

        {/* Search Bar */}
        <div className="relative w-full max-w-xl mx-auto mb-8">
          <Input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pr-10 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => {
            const isActive = 
              (!filters.category && !cat.value) || 
              (filters.category?.toLowerCase() === cat.value?.toLowerCase());
            return (
              <Button
                key={cat.value}
                onClick={() => handleCategoryChange(cat.value)}
                variant={isActive ? "default" : "outline"}
                className={
                  isActive
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "hover:bg-gray-100"
                }
              >
                {cat.label}
              </Button>
            );
          })}
        </div>

        {/* Loading State */}
        {loading ? (
          <ShopSkeleton />
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-6 sm:gap-8 mb-12">
            {products.map((product) => (
              <Card
                key={product.productId}
                className="overflow-hidden shadow-sm hover:shadow-md transition-shadow pt-0"
              >
                <CardContent className="p-0 relative">
                  <Link to={`/products/${product.productId}`}>
                    <img
                      src={product.image || "/placeholder.svg"}
                      alt={product.name}
                      className="w-full h-48 sm:h-52 md:h-56 object-cover"
                      onError={(e) => {
                        e.target.src = "/placeholder.svg";
                      }}
                    />
                    {product.discount > 0 && (
                      <span className="absolute top-3 left-3 bg-red-600 text-white text-xs sm:text-sm font-bold px-2 py-1 rounded-md shadow-md">
                        -{product.discount}%
                      </span>
                    )}
                  </Link>
                  <div className="p-4">
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(product.rating || 0)
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                      <span className="text-sm text-gray-500 ml-2">
                        ({product.reviews || 0})
                      </span>
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg text-gray-800 mb-1">
                      <Link to={`/products/${product.productId}`}>
                        {product.name}
                      </Link>
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {product.description}
                    </p>

                    {/* Price & Cart */}
                    <div className="flex items-center justify-between">
                      {product.discount > 0 ? (
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500 line-through">
                            {formatCurrency(product.price)}
                          </span>
                          <span className="text-lg sm:text-xl font-bold text-red-600">
                            {formatCurrency(
                              product.price -
                                (product.price * product.discount) / 100
                            )}
                          </span>
                          <span className="text-xs text-green-600 font-medium">
                            You save{" "}
                            {formatCurrency(
                              (product.price * product.discount) / 100
                            )}
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg sm:text-xl font-bold text-gray-900">
                          {formatCurrency(product.price)}
                        </span>
                      )}

                      <Button
                        onClick={() => handleAddToCart(product)}
                        className="bg-primary hover:bg-primary/90 text-white text-sm sm:text-base"
                        disabled={product.stock_quantity <= 0} // ✅ disable if out of stock
                      >
                        {product.stock_quantity <= 0
                          ? "Out of Stock"
                          : "Add to Cart"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-600 text-lg">
            No products found.
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-wrap justify-center items-center gap-2 mt-8">
            <Button
              variant="outline"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            {Array.from({ length: totalPages }, (_, i) => (
              <Button
                key={i + 1}
                variant={currentPage === i + 1 ? "default" : "outline"}
                onClick={() => handlePageChange(i + 1)}
                className={currentPage === i + 1 ? "bg-primary text-white" : ""}
              >
                {i + 1}
              </Button>
            ))}
            <Button
              variant="outline"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
